import { SpaceButtons } from '@ui/buttons/ButtonsFixed'
import Header from '@ui/elements/Header'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { View } from 'react-native'

import CouponsModal from '@order/modals/Discounts/Coupons/CouponsModal'
import CreditsModal from '@order/modals/Discounts/Credits/CreditsModal'
import ReferralModal from '@order/modals/Discounts/Referral/ReferralModal'

import { DiscountType, DiscountTypes } from '@ca/common'
import DiscountOption, {
  DiscountOptionStatus,
} from '@order/DiscountOption/DiscountOption'
import PriceSummary from '@order/PriceSummary'
import useAvailableCredits from '@order/hooks/useAvailableCredits'
import { useDealSettingsById } from '@order/hooks/useDealSettings'
import { useSavedOrderTotal } from '@order/hooks/useOrderTotal'
import {
  DiscountConfigDefaults,
  useDiscounts,
  useTotalDiscountAmount,
} from '@order/useDiscounts'
import { useConfigCondition } from 'features/config/useConfigCondition'
import { usePriceFormatter } from 'hooks/useCurrency'
import { useRemoteConfig } from 'hooks/useRemoteConfigStore'
import { ModalState } from './types'
import useSavedOrderState from '@order/hooks/useSavedOrderState'
import { useUniqPromoCodes } from '@order/modals/Discounts/Coupons/useUniqPromoCodes'

export default function Discounts({
  savedOrderId,
  children,
}: {
  savedOrderId?: string
  children?: React.ReactNode
}) {
  const { t } = useTranslation(['ui', 'modals', 'order'])
  const text = 'order:discounts'
  const [modal, setModal] = useState<ModalState>(null)
  const resetModal = () => setModal(null)

  const config = useRemoteConfig()
  const discounts = useDiscounts(savedOrderId, {})

  const { orderTotal, deals } = useSavedOrderTotal(savedOrderId)
  const dealSettings = useDealSettingsById(deals?.base?.dealId)

  const { result } = useSavedOrderState(savedOrderId)
  const promotions = result?.promotions
  const { discounted, discountedDisplay, hasDiscounts, base } =
    useTotalDiscountAmount({
      orderTotal,
      discounts: discounts.items,
    })

  const hiddenOptions: Partial<Record<DiscountType, boolean>> = {
    'gift-card': useConfigCondition('discounts:gift-card.hidden'),
  }

  const formatter = usePriceFormatter()
  const credits = useAvailableCredits()

  const promoArr = useMemo(() => {
    return Object.values(promotions ?? {})
  }, [promotions])

  const promoCodesToShow = useUniqPromoCodes(promoArr)

  // const customerCredits = credits.total || 0

  return (
    <>
      <Header
        title={t(`${text}.header.title`)}
        desc={t(`${text}.header.desc`)}
      />
      <View className="flex flex-col gap-8 px-8">
        {DiscountTypes.map((option) => {
          const isHidden = hiddenOptions[option] || !discounts[option]?.visible

          if (isHidden) {
            return null
          }

          if (option === 'promotion' && promoCodesToShow.length === 0) {
            return null
          }

          const items = discounts[option]?.items || []
          const isUsed = items.length > 0

          const region =
            config?.region?.[option] || DiscountConfigDefaults[option]
          const max = region.max === -1 ? Infinity : region.max
          const isAllowed = max > 0
          const isEnabled = discounts[option]?.enabled
          const isDisabled =
            !isAllowed ||
            !isEnabled ||
            (option === 'credit' && credits.total === 0)

          const status: DiscountOptionStatus = isUsed
            ? 'active'
            : isDisabled
            ? 'disabled'
            : 'default'

          const price = items.reduce((acc, item) => acc + item.discount, 0)

          const info: Record<DiscountType, string> = {
            credit: formatter.format(credits.available),
            coupon: items.length.toString(),
            'gift-card': items.length.toString(),
            referral: items.length.toString(),
            promotion: promoCodesToShow
              .map(({ description }) => description)
              ?.join(', '),
          }

          return (
            <DiscountOption
              key={option}
              option={option}
              status={
                status === 'default' && option === 'promotion'
                  ? 'attention'
                  : status
              }
              info={info[option]}
              price={price}
              onPress={() => setModal(option)}
            />
          )
        })}
      </View>
      <PriceSummary
        template={hasDiscounts ? 'discounts' : 'current'}
        price={discountedDisplay}
        installment={
          orderTotal.showMonthly ? dealSettings.priceSuffix : undefined
        }
        priceBefore={hasDiscounts ? base : undefined}
        textBottom={
          orderTotal.showMonthly
            ? t('summary:price.installments.total', {
                price: formatter.format(discounted),
                installments: orderTotal.installmentCount,
              })
            : undefined
        }
      />
      <SpaceButtons />
      {children}
      <CreditsModal
        savedOrderId={savedOrderId}
        open={modal === 'credit'}
        close={resetModal}
      />
      <CouponsModal
        mode="coupon"
        savedOrderId={savedOrderId}
        open={modal === 'coupon'}
        close={resetModal}
      />
      <CouponsModal
        mode="gift-card"
        savedOrderId={savedOrderId}
        open={modal === 'gift-card'}
        close={resetModal}
      />
      <CouponsModal
        mode="promotion"
        savedOrderId={savedOrderId}
        open={modal === 'promotion'}
        close={resetModal}
        codes={Object.values(promotions ?? {})}
      />
      <ReferralModal
        savedOrderId={savedOrderId}
        open={modal === 'referral'}
        close={resetModal}
      />
    </>
  )
}
